import { matchPath } from 'react-router-dom';
import {
    AnswerSurveyPage,
    AnswerTestPage,
    AssetsPage,
    AuthConflictPage,
    BillingPage,
    CustomSurveyBuilderPage,
    DashboardPage,
    FeatureFlagsAdminPage,
    HelpArticlePage,
    HelpPage,
    HelpTopicPage,
    PatientAnamnesisPage,
    PatientDetailPage,
    PatientsPage,
    PrivacyPolicyPage,
    ProfilePage,
    ReportPage,
    ReportTemplatePreviewPage,
    SignInPage,
    SignUpPage,
    SubscribePage,
    SupportInboxPage,
    SupportPage,
    SupportTicketPage,
    SurveyPreviewPage,
    SurveyResponsePage,
    SurveysPage,
    TermsOfUsePage,
    TestPreviewPage,
    TestResponsePage,
    ToolsPageRoute,
    VerifyEmailPage,
    WelcomePage,
} from '@/pages/constants';

/**
 * Single source of truth for "which route is this?".
 *
 * Two consumers depend on it and must never disagree: the document title, and
 * the analytics URL sanitizer. Concrete pathnames carry patient and report
 * UUIDs (`/patients/8f3a.../anamnesis`), so analytics may only ever transmit
 * the *pattern* (`/patients/:patientId/anamnesis`). Keeping one table means a
 * new route cannot be titled but left unsanitized.
 */

export const APP_TITLE = 'LaudoPsi';

/** Sent instead of the real path when a pathname matches no known route. */
export const UNKNOWN_ROUTE = '/unknown';

export const DEV_RECORDING_SESSION_PAGE = 'dev/recording-session';

type RouteMeta = {
    path: string;
    title: string;
};

const DEV_ROUTE_METAS: RouteMeta[] = import.meta.env.DEV
    ? [
          {
              path: `/${DEV_RECORDING_SESSION_PAGE}`,
              title: 'Sessão de Gravação',
          },
      ]
    : [];

export const ROUTE_METAS: RouteMeta[] = [
    { path: '/', title: 'Painel' },
    { path: `/${DashboardPage}`, title: 'Painel' },
    { path: `/${ProfilePage}`, title: 'Perfil' },
    { path: `/${PatientsPage}`, title: 'Pacientes' },
    { path: `/${PatientDetailPage}`, title: 'Paciente' },
    { path: `/${PatientAnamnesisPage}`, title: 'Anamnese' },
    { path: `/${ReportPage}`, title: 'Laudo' },
    { path: `/${BillingPage}`, title: 'Assinatura' },
    { path: `/${SurveysPage}`, title: 'Formulários' },
    { path: `/${SurveyPreviewPage}`, title: 'Prévia de Formulário' },
    { path: `/${CustomSurveyBuilderPage}`, title: 'Editor de Formulário' },
    { path: `/${SurveyResponsePage}`, title: 'Respostas do Formulário' },
    { path: `/${TestPreviewPage}`, title: 'Prévia de Instrumento' },
    { path: `/${TestResponsePage}`, title: 'Respostas do Instrumento' },
    { path: `/${AssetsPage}`, title: 'Instrumentos' },
    { path: `/${ToolsPageRoute}`, title: 'Calculadoras' },
    { path: `/${HelpPage}`, title: 'Ajuda' },
    { path: `/${HelpTopicPage}`, title: 'Ajuda' },
    { path: `/${HelpArticlePage}`, title: 'Ajuda' },
    { path: `/${SupportPage}`, title: 'Suporte' },
    { path: `/${SupportInboxPage}`, title: 'Inbox de Suporte' },
    { path: `/${SupportTicketPage}`, title: 'Chamado de Suporte' },
    { path: `/${FeatureFlagsAdminPage}`, title: 'Feature Flags' },
    ...DEV_ROUTE_METAS,
    { path: `/${WelcomePage}`, title: 'Boas-vindas' },
    { path: `/${ReportTemplatePreviewPage}`, title: 'Prévia de Template' },
    { path: `/${SignInPage}`, title: 'Entrar' },
    { path: `/${SignUpPage}`, title: 'Criar Conta' },
    { path: `/${VerifyEmailPage}`, title: 'Verificar E-mail' },
    { path: `/${AuthConflictPage}`, title: 'Conflito de Acesso' },
    { path: `/${PrivacyPolicyPage}`, title: 'Política de Privacidade' },
    { path: `/${TermsOfUsePage}`, title: 'Termos de Uso' },
    { path: `/${AnswerSurveyPage}`, title: 'Responder Formulário' },
    { path: `/${AnswerTestPage}`, title: 'Responder Instrumento' },
    { path: `/${SubscribePage}`, title: 'Assinatura' },
];

/**
 * Routes rendered for *patients*, not for our customers. The people on these
 * pages are data subjects who never agreed to anything with us, so analytics
 * is never initialized there. See docs/analytics.md (R6).
 */
const PATIENT_FACING_ROUTES = [
    `/${AnswerSurveyPage}`,
    `/${AnswerTestPage}`,
    `/${TestResponsePage}`,
];

/**
 * Surfaces we deliberately never push to desktop. Support is triage-from-
 * anywhere on both sides: telling a psychologist mid-problem — or our own agent
 * working the queue — to go find a computer is worse than a small screen.
 */
const DESKTOP_RECOMMENDATION_SUPPRESSED_ROUTES = [
    `/${SupportPage}`,
    `/${SupportInboxPage}`,
    `/${SupportTicketPage}`,
];

const matchesAny = (routes: string[], pathname: string) =>
    routes.some((path) => matchPath({ path, end: true }, pathname) !== null);

export const getRouteMeta = (pathname: string): RouteMeta | null =>
    ROUTE_METAS.find(({ path }) => matchPath({ path, end: true }, pathname)) ?? null;

/**
 * Returns the route *pattern* for a concrete pathname, or `UNKNOWN_ROUTE` when
 * nothing matches. Never returns the input pathname: falling back to the raw
 * value is exactly how a patient UUID would leak into analytics.
 */
export const matchRoutePattern = (pathname: string): string =>
    getRouteMeta(pathname)?.path ?? UNKNOWN_ROUTE;

export const isPatientFacingRoute = (pathname: string): boolean =>
    matchesAny(PATIENT_FACING_ROUTES, pathname);

export const isDesktopRecommendationSuppressedRoute = (pathname: string): boolean =>
    matchesAny(DESKTOP_RECOMMENDATION_SUPPRESSED_ROUTES, pathname);

export const getDocumentTitle = (pathname: string): string => {
    const routeTitle = getRouteMeta(pathname)?.title;

    return routeTitle ? `${APP_TITLE} | ${routeTitle}` : APP_TITLE;
};
